import type { Protocol } from "@zend/content";
import { livingUnits, other, status, type BattleState, type Unit } from "./state.js";

const SLOT_ORDER: Record<string, number> = { f1: 0, f2: 1, f3: 2, f4: 3, extra: 4, commander: 5, s1: 6, s2: 7, turret: 8 };

export function bySlot(a: Unit, b: Unit): number {
  return (SLOT_ORDER[a.slot] ?? 9) - (SLOT_ORDER[b.slot] ?? 9) || a.input.id.localeCompare(b.input.id);
}

/**
 * Targetable pool of the enemy side in priority layers: fighters, then commander, then supports, then turrets
 * (40-combat.md R31). Returns the first non-empty layer.
 */
export function targetPool(state: BattleState, side: Unit["side"]): Unit[] {
  const enemy = state.sides[other(side)];
  const fighters = livingUnits(enemy, "fighter").sort(bySlot);
  if (fighters.length) return fighters;
  const commander = livingUnits(enemy, "commander");
  if (commander.length) return commander;
  const supports = livingUnits(enemy, "support").sort(bySlot);
  if (supports.length) return supports;
  return livingUnits(enemy, "turret").sort(bySlot);
}

/** Rear selector: commander or a support directly, falling back to the normal pool. */
export function rearPool(state: BattleState, side: Unit["side"]): Unit[] {
  const enemy = state.sides[other(side)];
  const commander = livingUnits(enemy, "commander");
  if (commander.length) return commander;
  const supports = livingUnits(enemy, "support").sort(bySlot);
  if (supports.length) return supports;
  return targetPool(state, side);
}

/** The protocol a unit attacks with: its own for commanders, its living commander's for fighters, else front. */
export function activeProtocol(state: BattleState, u: Unit): Protocol {
  if (u.protocol) return u.protocol;
  const commander = state.sides[u.side].units.find((c) => c.kind === "commander" && c.alive);
  return commander?.protocol ?? "front";
}

export function applyProtocol(state: BattleState, protocol: Protocol, pool: Unit[]): Unit {
  const first = pool[0]!;
  const roleFirst = (role: string) => pool.find((p) => p.meta.role === role) ?? first;
  switch (protocol) {
    case "front":
      return first;
    case "lowest_hp":
      return pool.reduce((m, p) => (p.hp < m.hp ? p : m), first);
    case "lowest_hp_pct":
      return pool.reduce((m, p) => (p.hp / p.maxHp < m.hp / m.maxHp ? p : m), first);
    case "highest_atk":
      return pool.reduce((m, p) => (p.stats.atk > m.stats.atk ? p : m), first);
    case "highest_def":
      return pool.reduce((m, p) => (p.stats.def > m.stats.def ? p : m), first);
    case "defender_first":
      return roleFirst("defender");
    case "saboteur_first":
      return roleFirst("saboteur");
    case "attacker_first":
      return roleFirst("attacker");
    case "random":
      return state.rng.pick(pool);
    case "marked_first":
      return pool.find((p) => status(p, "marked")) ?? first;
    default:
      return first;
  }
}

/** Picks an enemy: taunt first, then the protocol over the layered pool. */
export function pickEnemy(state: BattleState, actor: Unit, selector: "protocol" | "rear" | "lowest_hp_pct" | "random" = "protocol"): Unit | null {
  const pool = selector === "rear" ? rearPool(state, actor.side) : targetPool(state, actor.side);
  if (pool.length === 0) return null;
  const selected = (target: Unit, reason: string) => {
    if (state.rulesVersion === 2) state.events.push({ t: state.tick, k: "target", a: actor.input.id, b: target.input.id, x: reason });
    return target;
  };
  if (selector !== "rear") {
    const taunting = pool.find((p) => status(p, "taunt"));
    if (taunting) return selected(taunting, "taunt");
  }
  if (selector === "lowest_hp_pct") return selected(applyProtocol(state, "lowest_hp_pct", pool), selector);
  if (selector === "random") return selected(state.rng.pick(pool), selector);
  if (selector === "rear") return selected(pool[0]!, selector);
  const protocol = activeProtocol(state, actor);
  return selected(applyProtocol(state, protocol, pool), protocol);
}

/** Living enemies for multi-target abilities, front first. */
export function enemies(state: BattleState, actor: Unit): Unit[] {
  const enemy = state.sides[other(actor.side)];
  return [...livingUnits(enemy, "fighter"), ...livingUnits(enemy, "commander"), ...livingUnits(enemy, "support")].sort(bySlot);
}

export function allies(state: BattleState, actor: Unit, includeSelf = true): Unit[] {
  return state.sides[actor.side].units.filter((u) => u.alive && u.kind !== "turret" && (includeSelf || u !== actor)).sort(bySlot);
}

export function weakestAlly(state: BattleState, actor: Unit): Unit | null {
  const pool = allies(state, actor);
  if (pool.length === 0) return null;
  return pool.reduce((m, p) => (p.hp / p.maxHp < m.hp / m.maxHp ? p : m), pool[0]!);
}

export function strongestFighter(state: BattleState, actor: Unit): Unit | null {
  const pool = allies(state, actor).filter((u) => u.kind === "fighter");
  if (pool.length === 0) return null;
  return pool.reduce((m, p) => (p.stats.atk > m.stats.atk ? p : m), pool[0]!);
}

export function deadFighter(state: BattleState, actor: Unit): Unit | null {
  return state.sides[actor.side].units.filter((u) => !u.alive && u.kind === "fighter").sort(bySlot)[0] ?? null;
}
